import { AlertTriangle, Trash2 } from 'lucide-react-native';
import { useCallback } from 'react';
import { FlatList, type ListRenderItemInfo, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Badge } from '../../../components/primitives/Badge';
import { Button } from '../../../components/primitives/Button';
import { IconButton } from '../../../components/primitives/IconButton';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { LinkedInSearchSegment } from '../types';

interface SegmentListProps {
  segments: LinkedInSearchSegment[];
  onExecute: (segment: LinkedInSearchSegment) => void;
  onDelete: (segment: LinkedInSearchSegment) => void;
  onViewResults: (segment: LinkedInSearchSegment) => void;
  isExecuting?: string;
}

function getStatusBadgeColor(status: string): 'blue' | 'green' | 'red' | 'neutral' {
  switch (status) {
    case 'running':
      return 'blue';
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    default:
      return 'neutral';
  }
}

export function SegmentList({
  segments,
  onExecute,
  onDelete,
  onViewResults,
  isExecuting,
}: Readonly<SegmentListProps>) {
  const { t } = useTranslation();

  const getStatusLabel = (status: string) => {
    const key = `linkedinSearch.statuses.${status}`;
    return t(key);
  };

  const renderSegment = useCallback(
    ({ item }: ListRenderItemInfo<LinkedInSearchSegment>) => {
      const criteria = item.criteria;
      const criteriaLabels: string[] = [];
      if (criteria.keywords) criteriaLabels.push(criteria.keywords);
      if (criteria.location) criteriaLabels.push(criteria.location);
      if (criteria.industry) criteriaLabels.push(criteria.industry);
      if (criteria.roleFilter) criteriaLabels.push(criteria.roleFilter);

      const isRunning = item.status === 'running' || isExecuting === item.id;

      return (
        <Box
          borderRadius="lg"
          padding="md"
          backgroundColor={colors.background.tertiary}
          style={styles.card}
        >
          <VStack spacing="sm">
            <HStack spacing="sm" style={styles.header}>
              <VStack spacing="xxs" style={styles.titleContainer}>
                <Text variant="body" color={colors.text.primary} style={styles.title}>
                  {item.name}
                </Text>
                {criteriaLabels.length > 0 && (
                  <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
                    {criteriaLabels.join(' | ')}
                  </Text>
                )}
              </VStack>
              <Badge color={getStatusBadgeColor(item.status)} variant="subtle">
                {getStatusLabel(item.status)}
              </Badge>
            </HStack>

            {item.totalResults > 0 && (
              <HStack spacing="lg">
                <VStack spacing="xxs">
                  <Text variant="caption" color={colors.text.disabled}>
                    {t('linkedinSearch.segments.found')}
                  </Text>
                  <Text variant="body" color={colors.text.primary}>
                    {item.totalResults}
                  </Text>
                </VStack>
                <VStack spacing="xxs">
                  <Text variant="caption" color={colors.text.disabled}>
                    {t('linkedinSearch.segments.enriched')}
                  </Text>
                  <Text variant="body" color={colors.status.success.main}>
                    {item.enrichedCount}
                  </Text>
                </VStack>
                {item.failedCount > 0 && (
                  <VStack spacing="xxs">
                    <Text variant="caption" color={colors.text.disabled}>
                      {t('linkedinSearch.segments.failed')}
                    </Text>
                    <Text variant="body" color={colors.status.error.main}>
                      {item.failedCount}
                    </Text>
                  </VStack>
                )}
              </HStack>
            )}

            {item.status === 'failed' && (
              <HStack spacing="xs" align="center">
                <AlertTriangle size={14} color={colors.status.error.main} strokeWidth={2} />
                <Text variant="caption" color={colors.text.secondary} style={styles.failedHint}>
                  {t('linkedinSearch.segments.failedHint')}
                </Text>
              </HStack>
            )}

            <HStack spacing="sm" style={styles.actions}>
              <Button
                onPress={() => onExecute(item)}
                variant="primary"
                size="sm"
                disabled={isRunning}
                accessibilityLabel={`Execute segment ${item.name}`}
              >
                {isRunning
                  ? t('linkedinSearch.segments.running')
                  : t('linkedinSearch.segments.execute')}
              </Button>
              {item.totalResults > 0 && (
                <Button
                  onPress={() => onViewResults(item)}
                  variant="secondary"
                  size="sm"
                  accessibilityLabel={`View results for ${item.name}`}
                >
                  {t('linkedinSearch.segments.viewResults')}
                </Button>
              )}
              <Box style={styles.spacer} />
              <IconButton
                icon={<Trash2 size={16} color={colors.status.error.main} />}
                size="sm"
                variant="ghost"
                onPress={() => onDelete(item)}
                accessibilityLabel={t('linkedinSearch.segments.deleteSegment')}
              />
            </HStack>
          </VStack>
        </Box>
      );
    },
    [isExecuting, onExecute, onViewResults, onDelete, t, getStatusLabel],
  );

  return (
    <FlatList
      data={segments}
      keyExtractor={(item) => item.id}
      renderItem={renderSegment}
      contentContainerStyle={styles.list}
      accessibilityRole="list"
      accessibilityLabel="LinkedIn search segments"
    />
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 12,
    paddingBottom: 24,
  },
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  header: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    fontWeight: '600',
  },
  actions: {
    alignItems: 'center',
    paddingTop: 4,
  },
  failedHint: {
    flex: 1,
  },
  spacer: {
    flex: 1,
  },
});
